import type { ProjectId, RoleId, UserId } from '../shared/ids';
import type { PermissionKey } from './permissions';

/**
 * Atribuição de papel a um usuário.
 * `projectId` ausente = papel **global**, válido em todas as obras (RN-133).
 */
export interface RoleAssignment {
  readonly userId: UserId;
  readonly roleId: RoleId;
  readonly projectId?: ProjectId;
}

export interface RoleWithPermissions {
  readonly id: RoleId;
  readonly key: string;
  readonly permissions: readonly PermissionKey[];
}

/**
 * Permissão efetiva do usuário para uma obra (RN-133).
 *
 * `permissões dos papéis globais` ∪ `permissões dos papéis daquela obra`.
 *
 * Quando `projectId` é `undefined`, a operação é de sistema (perfis, backup, catálogos
 * globais) e **apenas** os papéis globais contam — um papel concedido em uma obra
 * específica não pode administrar o sistema inteiro.
 */
export function resolveEffectivePermissions(
  assignments: readonly RoleAssignment[],
  rolesById: ReadonlyMap<RoleId, RoleWithPermissions>,
  projectId?: ProjectId,
): ReadonlySet<PermissionKey> {
  const effective = new Set<PermissionKey>();

  for (const assignment of assignments) {
    const isGlobal = assignment.projectId === undefined;
    const matchesProject = projectId !== undefined && assignment.projectId === projectId;
    if (!isGlobal && !matchesProject) continue;

    const role = rolesById.get(assignment.roleId);
    if (!role) continue;

    for (const permission of role.permissions) effective.add(permission);
  }

  return effective;
}

/**
 * Permissões que o usuário tem em **alguma** obra.
 *
 * Serve a uma pergunta só, e de navegação: "esta tela faz sentido para este perfil?". O
 * encarregado da Obra B não deve ver o menu de Cronograma sumir só porque a obra ativa no
 * seletor é a A — ele veria o item aparecer e desaparecer conforme troca de obra, sem
 * entender por quê.
 *
 * **Não serve para autorizar nada.** Autorização é sempre por obra (RN-132, RN-133), e
 * quem decide é `resolveEffectivePermissions` com o escopo em mãos.
 */
export function permissionsAnywhere(
  assignments: readonly RoleAssignment[],
  rolesById: ReadonlyMap<RoleId, RoleWithPermissions>,
): ReadonlySet<PermissionKey> {
  const effective = new Set<PermissionKey>();
  for (const assignment of assignments) {
    const role = rolesById.get(assignment.roleId);
    if (!role) continue;
    for (const permission of role.permissions) effective.add(permission);
  }
  return effective;
}

/**
 * O usuário alcança esta obra? (RN-132)
 *
 * Verificado **antes** de ler qualquer registro, para não vazar a existência da obra
 * por diferença de erro.
 */
export function hasProjectAccess(
  assignments: readonly RoleAssignment[],
  projectId: ProjectId,
): boolean {
  return assignments.some(
    (assignment) => assignment.projectId === undefined || assignment.projectId === projectId,
  );
}

/** Obras explicitamente atribuídas. Vazio quando o usuário só tem papéis globais. */
export function scopedProjectIds(assignments: readonly RoleAssignment[]): ReadonlySet<ProjectId> {
  const ids = new Set<ProjectId>();
  for (const assignment of assignments) {
    if (assignment.projectId !== undefined) ids.add(assignment.projectId);
  }
  return ids;
}

/** O usuário tem algum papel global — logo, alcança todas as obras. */
export function hasGlobalScope(assignments: readonly RoleAssignment[]): boolean {
  return assignments.some((assignment) => assignment.projectId === undefined);
}
